import uuid from 'react-uuid';
import { create } from 'zustand';

import AMP_KEYS from '@/configs/amp-track-keys';
import {
  HEY_SYNCLY_ANSWER_STATUS,
  HEY_SYNCLY_MESSAGE_TYPE,
  HEY_SYNCLY_SOURCE_TYPE,
  HEY_SYNCLY_STATUS,
  type HeySynclyAnswer,
  type HeySynclyAnswerSocketPayload,
  type HeySynclyChat,
  type HeySynclyChatAskRequestParams,
  type HeySynclyChatVoteRequestParams,
  heySynclyService,
} from '@/entities/hey-syncly';
import type { AnalysisGptSocketInfo } from '@/features/Analysis/types';
import type SFilter from '@/features/StructuredFilter/class/SFilter';
import { STUDIO_ITEM_STATUS } from '@/features/Studio/model';
import service from '@/services';
import {
  GPT_DOMAIN,
  type GptSocketBasePayload,
  type GptSocketInfo,
} from '@/types/gptConst';
import { track } from '@/utils/track/track';

import useSocketStore from './SocketStore';

type State = {
  chatMap: Map<Alias, HeySynclyChat>;
  selectedMessageId: Alias | null;
  tempAnswer: HeySynclyAnswer | null;
  studioItemDetail: Alias | null;
  initialFilter: SFilter | null;
};

type Actions = {
  newChat: (initialFilter?: SFilter | null) => void;
  askSubmit: (
    params: Omit<HeySynclyChatAskRequestParams, 'socket_id'>,
  ) => Promise<HeySynclyChat>;
  selectChat: (params: {
    from?: Alias;
    to: Alias;
    message_id?: Alias;
  }) => Promise<void>;
  deleteChat: (chat_alias: Alias) => Promise<void>;
  leaveChat: (chat_alias: Alias) => void;
  updateAnswerBySocket: (socketPayload: HeySynclyAnswerSocketPayload) => void;
  voteMessage: (params: HeySynclyChatVoteRequestParams) => Promise<void>;
  setStudioItemDetail: (item_alias: Alias) => void;
  clearStudioItemDetail: () => void;
  initialCurrentChat: () => void;
  setState: StoreSetter<State>;
  initHeySynclyStore: () => void;

  // GPT Store migration
  askSubmitByFeedbackList: (
    socketPaylaod: GptSocketInfo,
  ) => Promise<HeySynclyChat>;
  askSubmitByAnalysis: (
    dashboard_alias: string,
    payload: AnalysisGptSocketInfo,
  ) => Promise<HeySynclyChat>;
};

const initialState: State = {
  chatMap: new Map(),
  selectedMessageId: null,
  tempAnswer: null,
  studioItemDetail: null,
  initialFilter: null,
};

const useHeySynclyStore = create<State & Actions>((set, get) => ({
  ...initialState,
  askSubmit: async (params) => {
    const socket = useSocketStore.getState().socket;
    if (!socket) throw new Error('socket is required');
    const res = await heySynclyService.AskHeySynclyChat({
      ...params,
      socket_id: socket.id,
    });
    const prevMap = new Map(get().chatMap);
    prevMap.set(res.alias, res);
    set({
      chatMap: prevMap,
      tempAnswer: {
        chat_alias: res.alias,
        status: HEY_SYNCLY_ANSWER_STATUS.UNDERSTANDING,
        answer: '',
        item: params.item_type
          ? {
              type: params.item_type,
              id: uuid(),
              title: 'New Report',
              status: STUDIO_ITEM_STATUS.PENDING,
            }
          : undefined,
      },
    });
    return res;
  },
  setStudioItemDetail: (item_alias: Alias) => {
    set({ studioItemDetail: item_alias });
  },
  clearStudioItemDetail: () => {
    set({ studioItemDetail: null });
  },
  updateAnswerBySocket: async (socketPayload) => {
    const prevMap = new Map(get().chatMap);
    const existingChat = prevMap.get(socketPayload.chat_alias);
    if (!existingChat) return console.error('no matched chat');

    if (socketPayload.status === HEY_SYNCLY_ANSWER_STATUS.COMPLETE) {
      // COMPLETE 상태일 때에는 chat 전체 데이터를 fetching 해서 업데이트
      const chat = await heySynclyService.GetHeySynclyChat(
        socketPayload.chat_alias,
      );

      // Track answer received event
      const lastAnswerMessage = chat.messages
        .filter((msg) => msg.type === HEY_SYNCLY_MESSAGE_TYPE.ANSWER)
        .pop();

      if (lastAnswerMessage) {
        track(AMP_KEYS.HEY_SYNCLY_ANSWER_RECEIVED, {
          chat_alias: chat.alias,
          message_id: lastAnswerMessage.alias,
          feedback_analyzed_count: lastAnswerMessage.feedback_count,
        });
      }

      prevMap.set(chat.alias, chat);

      const nextStudioItemDetail = lastAnswerMessage?.item?.id;

      set({
        chatMap: prevMap,
        tempAnswer: null,
        ...(nextStudioItemDetail
          ? { studioItemDetail: nextStudioItemDetail }
          : {}),
      });
    } else {
      switch (socketPayload.status) {
        case HEY_SYNCLY_ANSWER_STATUS.REASONING: {
          let reason = get().tempAnswer?.reason || '';
          reason += socketPayload.reason || '';
          reason = reason.replaceAll('\n\n', '\n');
          set({
            tempAnswer: {
              chat_alias: get().tempAnswer?.chat_alias || '',
              answer: '',
              status: HEY_SYNCLY_ANSWER_STATUS.REASONING,
              reason,
            },
          });
          break;
        }
        case HEY_SYNCLY_ANSWER_STATUS.GENERATING: {
          let answer = get().tempAnswer?.answer || '';
          answer += socketPayload.answer || '';
          set({
            tempAnswer: {
              chat_alias: get().tempAnswer?.chat_alias || '',
              answer,
              status: HEY_SYNCLY_ANSWER_STATUS.GENERATING,
              reason: get().tempAnswer?.reason,
            },
          });
          break;
        }
        default:
          set({ tempAnswer: socketPayload });
      }
    }
  },
  newChat: (initialFilter) => {
    set({
      chatMap: new Map(),
      tempAnswer: null,
      studioItemDetail: null,
      selectedMessageId: null,
      initialFilter,
    });
  },
  voteMessage: async (params: HeySynclyChatVoteRequestParams) => {
    await heySynclyService.VoteHeySynclyChat(params);
    const prevMap = new Map(get().chatMap);
    const targetChat = prevMap.get(params.chat_alias);

    if (!targetChat) return console.error('no matched chat');

    const messageIndex = targetChat.messages.findIndex(
      (message) => message.alias === params.message_id,
    );
    if (messageIndex === -1) return console.error('message not found');

    const newMessages = [...targetChat.messages];

    newMessages.splice(messageIndex, 1, {
      ...newMessages[messageIndex],
      vote_up: params.vote,
    });

    const newChatObj = {
      ...targetChat,
      messages: newMessages,
    };

    prevMap.set(targetChat.alias, newChatObj);

    set({ chatMap: prevMap });
  },
  selectChat: async ({ from, to, message_id }) => {
    const prevMap = new Map(get().chatMap);
    if (from) prevMap.delete(from);
    const chat = await heySynclyService.GetHeySynclyChat(to);
    prevMap.set(chat.alias, chat);
    if (chat.status === HEY_SYNCLY_STATUS.PENDING) {
      const existingPendingItem =
        get().tempAnswer?.chat_alias === chat.alias
          ? get().tempAnswer?.item
          : undefined;

      set({
        chatMap: prevMap,
        tempAnswer: {
          chat_alias: chat.alias,
          status: HEY_SYNCLY_ANSWER_STATUS.UNDERSTANDING,
          answer: '',
          ...(existingPendingItem ? { item: existingPendingItem } : {}),
        },
        selectedMessageId: message_id || null,
        studioItemDetail: null,
        initialFilter: null,
      });
    } else {
      set({
        chatMap: prevMap,
        tempAnswer: null,
        studioItemDetail: null,
        selectedMessageId: message_id || null,
        initialFilter: null,
      });
    }
  },
  deleteChat: async (chat_alias: Alias) => {
    await heySynclyService.DeleteHeySynclyChat(chat_alias);
    const prevMap = new Map(get().chatMap);
    prevMap.delete(chat_alias);
    if (prevMap.has(chat_alias)) {
      get().initialCurrentChat();
    }
  },
  leaveChat: (chat_alias: Alias) => {
    const prevMap = new Map(get().chatMap);
    prevMap.delete(chat_alias);
    set({ chatMap: prevMap });
  },
  initialCurrentChat: () => {
    set({
      tempAnswer: null,
      studioItemDetail: null,
      selectedMessageId: null,
      initialFilter: null,
    });
  },
  setState: set,
  initHeySynclyStore: () => set(initialState),

  askSubmitByFeedbackList: async (paylaod) => {
    const querySubmit = async (): Promise<HeySynclyChat> => {
      const socket = useSocketStore.getState().socket;
      if (!socket) throw new Error('socket is required');

      const basePayload: GptSocketBasePayload = {
        question: paylaod.question,
        socket_id: socket.id,
      };

      track(AMP_KEYS.ASK_SYNCLY_SUBMIT, {
        domain: paylaod.domain,
      });

      switch (paylaod.domain) {
        case GPT_DOMAIN.GLOBAL_SEARCH: {
          if (!paylaod.search) throw new Error('search is required');
          const res = await service.gpt.requestSearchAsk(
            paylaod.search.alias,
            basePayload,
            paylaod.search.filterPayload,
          );
          return res;
        }
        case GPT_DOMAIN.GLOBAL_SEARCH_SUMMARY: {
          if (!paylaod.search_summary)
            throw new Error('search_summary is required');
          const res = await service.gpt.requestSearchSummaryAsk(
            basePayload,
            paylaod.search_summary,
          );
          return res;
        }
        case GPT_DOMAIN.INSIGHT_EXPLORER: {
          if (!paylaod.insight_explorer)
            throw new Error('insight_explorer is required');
          const res = await heySynclyService.AskHeySynclyFromOtherSource(
            basePayload,
            paylaod.insight_explorer || {},
            HEY_SYNCLY_SOURCE_TYPE.INSIGHT,
          );
          return res;
        }
        case GPT_DOMAIN.SIGNAL: {
          const res = await heySynclyService.AskHeySynclyFromOtherSource(
            basePayload,
            paylaod.signal || {},
            HEY_SYNCLY_SOURCE_TYPE.SIGNAL,
          );
          return res;
        }

        default:
          throw new Error(`[Invalid domain]: ${paylaod.domain}`);
      }
    };

    const res = await querySubmit();

    const prevMap = new Map(get().chatMap);
    prevMap.set(res.alias, res);
    set({
      chatMap: prevMap,
      tempAnswer: {
        chat_alias: res.alias,
        status: HEY_SYNCLY_ANSWER_STATUS.UNDERSTANDING,
        answer: '',
      },
    });

    return res;
  },
  askSubmitByAnalysis: async (dashboard_alias, payload) => {
    const socket = useSocketStore.getState().socket;
    if (!socket) throw new Error('socket is required');

    const res = await service.gpt.requestAnalysisAsk(dashboard_alias, {
      chart_payload: payload.chart_payload,
      question_type: payload.question_type,
      question: payload.question,
      socket_id: socket.id,
    });

    // Store chart_id and start time for tracking
    const chart_id = payload.chart_payload?.chart_id;
    if (chart_id) {
      const startTime = Date.now();
      // We'll use Dashboard2Store to track analysis request times
      const { default: useDashboard2Store } = await import('./Dashboard2Store');
      useDashboard2Store.getState().setAnalysisRequestTime(chart_id, startTime);
    }
    const prevMap = new Map(get().chatMap);
    prevMap.set(res.alias, res);
    set({
      chatMap: prevMap,
      tempAnswer: {
        chat_alias: res.alias,
        status: HEY_SYNCLY_ANSWER_STATUS.UNDERSTANDING,
        answer: '',
      },
    });
    return res;
  },
}));

export default useHeySynclyStore;
